import styled, { keyframes, css } from "styled-components";

/* ═══════════════════════════════════════════════════════════════════════
   ReferralModal — Light Water redesign
   Frosted-white panel with aqua underglow, gradient title, frosted close
   circle, and a clean referral pill. Matches EnterPromptModal language.
   ═══════════════════════════════════════════════════════════════════════ */

const panelFloat = keyframes`
  0%, 100% { filter: brightness(1); }
  50%      { filter: brightness(1.02); }
`;

export const ModalOverlay = styled.div`
  position: fixed;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  z-index: 1000;
  background:
    radial-gradient(
      circle at 50% 30%,
      rgba(var(--color-brand-400-rgb), 0.18),
      transparent 55%
    ),
    rgba(15, 47, 84, 0.32);
  backdrop-filter: blur(18px) saturate(140%);
  -webkit-backdrop-filter: blur(18px) saturate(140%);
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transition: opacity 220ms ease;
`;

export const ModalContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 420px;
  border-radius: 28px;
  border: 1px solid var(--glass-edge);
  background:
    radial-gradient(
      120% 60% at 30% -10%,
      rgba(var(--glass-white-rgb), 0.96) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 55%,
      rgba(var(--glass-pale-rgb), 0.82) 100%
    ),
    linear-gradient(
      180deg,
      rgba(var(--glass-white-rgb), 0.94) 0%,
      rgba(var(--glass-pale-rgb), 0.9) 100%
    );
  backdrop-filter: blur(28px) saturate(180%);
  -webkit-backdrop-filter: blur(28px) saturate(180%);
  box-shadow:
    0 32px 64px -18px rgba(var(--color-brand-500-rgb), 0.4),
    0 12px 28px rgba(var(--color-brand-400-rgb), 0.22),
    inset 0 2px 0 rgba(255, 255, 255, 0.95),
    inset 0 -10px 22px rgba(var(--color-brand-200-rgb), 0.26),
    inset 0 0 0 1px rgba(255, 255, 255, 0.45);

  [data-theme="dark"] & {
    border-color: transparent;
    box-shadow:
      0 28px 56px -18px rgba(0, 0, 0, 0.6),
      0 10px 24px rgba(0, 0, 0, 0.4),
      inset 0 -10px 22px rgba(var(--color-brand-700-rgb), 0.18);
  }

  [data-theme="dark"] &::after {
    opacity: 0;
  }
  isolation: isolate;
  opacity: ${({ $isAnimating }) => ($isAnimating ? 1 : 0)};
  transform: ${({ $isAnimating }) =>
    $isAnimating ? "scale(1)" : "scale(0.96) translateY(18px)"};
  transition:
    opacity 260ms ease,
    transform 260ms var(--ease-snappy);
  animation: ${panelFloat} 5.5s ease-in-out infinite;

  /* Top specular sheen */
  &::before {
    content: "";
    position: absolute;
    top: 2px;
    left: 32px;
    right: 32px;
    height: 20px;
    border-radius: 999px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.9) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(2px);
    pointer-events: none;
    z-index: 10;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  /* Bottom aqua underglow */
  &::after {
    content: "";
    position: absolute;
    bottom: -8px;
    left: 25%;
    right: 25%;
    height: 16px;
    background: radial-gradient(
      ellipse at center top,
      rgba(var(--color-brand-400-rgb), 0.42) 0%,
      rgba(var(--color-brand-400-rgb), 0) 70%
    );
    pointer-events: none;
    z-index: -1;
  }

  [data-theme="dark"] & {
    border-color: transparent;
  }
`;

export const ModalContent = styled.div`
  position: relative;
  z-index: 1;
  padding: 26px 24px 24px;
  display: flex;
  flex-direction: column;
  gap: 16px;

  @media (max-width: 480px) {
    padding: 22px 18px 20px;
  }
`;

export const TitleContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  width: 100%;
`;

export const ModalTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-primary);
  font-size: 22px;
  font-weight: 800;
  line-height: normal;
  letter-spacing: -0.03em;
  text-align: left;

  /* Brand gradient title */
  background: linear-gradient(
    0deg,
    var(--color-text-strong) 0%,
    var(--color-text-body) 45%,
    var(--color-brand-600) 100%
  );
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  color: transparent;
  filter: drop-shadow(0 1px 2px rgba(var(--color-brand-600-rgb), 0.14));
`;

export const ModalSubtitle = styled.p`
  margin: 0;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 13px;
  font-weight: 500;
  letter-spacing: -0.005em;
  line-height: normal;
  text-align: left;
`;

export const ReferralLinkContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  margin-top: 4px;
`;

export const ReferralLink = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 44px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding: 10px 16px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-input);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);

  span {
    color: var(--color-brand-700);
    font-family: var(--font-family-primary);
    font-size: 13px;
    font-weight: 700;
    letter-spacing: -0.005em;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
`;

export const CopyButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: 999px;
  border: 1px solid var(--color-line);
  background: var(--color-surface-2);
  backdrop-filter: blur(16px) saturate(160%);
  -webkit-backdrop-filter: blur(16px) saturate(160%);
  box-shadow: var(--shadow-micro);
  color: var(--color-brand-700);
  cursor: pointer;
  outline: none;
  flex-shrink: 0;
  transition:
    transform 180ms var(--ease-snappy),
    border-color 180ms var(--ease-snappy),
    background 180ms var(--ease-snappy),
    color 180ms var(--ease-snappy);

  svg {
    width: 16px;
    height: 16px;
    color: currentColor;
    path {
      stroke: currentColor;
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: var(--color-line-strong);
  }

  ${({ $success }) =>
    $success &&
    css`
      color: #11a168;
      border-color: rgba(33, 189, 125, 0.45);
      background: rgba(33, 189, 125, 0.16);

      svg {
        color: #11a168;
      }
    `}
`;

export const LightText = styled.span`
  align-self: center;
  color: var(--color-text-muted);
  font-family: var(--font-family-primary);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
`;

/* Frosted-glass circle close button */
export const CloseButton = styled.button`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1px solid var(--glass-edge-soft);
  background: radial-gradient(
    130% 100% at 30% 15%,
    rgba(var(--glass-white-rgb), 0.78) 0%,
    rgba(var(--glass-white-rgb), 0.5) 45%,
    rgba(var(--glass-pale-rgb), 0.38) 100%
  );
  backdrop-filter: blur(18px) saturate(180%);
  -webkit-backdrop-filter: blur(18px) saturate(180%);
  box-shadow:
    0 6px 14px rgba(var(--color-brand-500-rgb), 0.14),
    inset 0 1px 0 rgba(255, 255, 255, 0.95),
    inset 0 -3px 8px rgba(var(--color-brand-200-rgb), 0.22);
  cursor: pointer;
  outline: none;
  transition:
    transform 200ms var(--ease-snappy),
    border-color 200ms var(--ease-snappy);

  [data-theme="dark"] & {
    border-color: transparent;
    background: var(--color-surface-3);
    box-shadow:
      0 4px 10px rgba(0, 0, 0, 0.45),
      inset 0 -2px 6px rgba(var(--color-brand-700-rgb), 0.16);
  }

  [data-theme="dark"] & svg path {
    stroke: var(--color-text-strong);
  }

  /* Top-left specular */
  &::before {
    content: "";
    position: absolute;
    top: 3px;
    left: 5px;
    width: 16px;
    height: 9px;
    border-radius: 50%;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.85) 0%,
      rgba(255, 255, 255, 0) 100%
    );
    filter: blur(1px);
    pointer-events: none;
  }

  [data-theme="dark"] &::before {
    opacity: 0;
  }

  svg {
    width: 14px;
    height: 14px;
    position: relative;
    z-index: 1;

    path {
      stroke: var(--color-brand-700);
    }
  }

  &:hover {
    transform: translateY(-1px);
    border-color: rgba(var(--color-brand-400-rgb), 0.55);
  }
`;
